import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Textarea,
  IconButton,
  Badge,
  Tooltip,
  useColorModeValue,
  Divider,
  Collapse,
  Button,
  Icon,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import {
  FiFileText,
  FiSave,
  FiClock,
  FiChevronDown,
  FiChevronUp,
  FiMaximize2,
  FiMinimize2,
  FiCopy,
  FiTrash2,
} from 'react-icons/fi';
import { supabase, isSupabaseConfigured } from '../../utils/supabase';
import moment from 'moment';

/**
 * DealNotepad - A persistent, ever-growing notepad for a deal.
 * 
 * Features:
 * - Auto-saves with debounce (1.5s after last keystroke)
 * - Manual save button
 * - Auto-appends entries when activities are logged
 * - Timestamps for auto-appended entries
 * - Expand/collapse
 * - Character count
 * - Copy all to clipboard
 * 
 * Data: Stored in crm_deals.scratchpad (text column, needs migration)
 * For POC: Uses local state with simulated save
 */
export default function DealNotepad({ 
  dealId, 
  initialContent = '', 
  onContentChange,
  isExpanded: controlledExpanded,
  onToggleExpand,
  minHeight = '300px',
  maxHeight = 'calc(100vh - 200px)',
}) {
  const [content, setContent] = useState(initialContent);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isFullHeight, setIsFullHeight] = useState(true);
  const saveTimerRef = useRef(null);
  const textareaRef = useRef(null);
  const toast = useToast();

  // Colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBg = useColorModeValue('gray.50', 'gray.750');
  const textareaBg = useColorModeValue('yellow.50', 'gray.900');
  const textareaFocusBg = useColorModeValue('white', 'gray.850');
  const savedColor = useColorModeValue('green.600', 'green.400');
  const unsavedColor = useColorModeValue('orange.500', 'orange.400');

  // Sync with external content changes (e.g., real-time updates)
  useEffect(() => {
    if (initialContent && initialContent !== content && !hasUnsavedChanges) {
      setContent(initialContent);
    }
  }, [initialContent]);

  // Debounced auto-save
  const triggerAutoSave = useCallback((newContent) => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      handleSave(newContent);
    }, 1500);
  }, [dealId]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
      }
    };
  }, []);

  const handleContentChange = (e) => {
    const newContent = e.target.value;
    setContent(newContent);
    setHasUnsavedChanges(true);
    onContentChange?.(newContent);
    triggerAutoSave(newContent);
  };

  const handleSave = async (contentToSave) => {
    const text = contentToSave ?? content;
    setIsSaving(true);
    
    try {
      if (isSupabaseConfigured() && dealId) {
        // Real save to Supabase
        const { error } = await supabase
          .from('crm_deals')
          .update({ scratchpad: text })
          .eq('id', dealId);
        
        if (error) throw error;
      }
      
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
    } catch (err) {
      console.error('Failed to save notepad:', err);
      toast({
        title: 'Failed to save notepad',
        description: err.message,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Append a timestamped entry (called externally when activity is logged)
  const appendEntry = useCallback((text, prefix = '') => {
    const timestamp = moment().format('MM/DD/YY h:mm A');
    const separator = content.trim() ? '\n\n' : '';
    const entry = `${separator}--- ${prefix ? `${prefix} ` : ''}[${timestamp}] ---\n${text}`;
    const newContent = content + entry;
    setContent(newContent);
    setHasUnsavedChanges(true);
    triggerAutoSave(newContent);
    
    // Scroll to bottom
    if (textareaRef.current) {
      setTimeout(() => {
        textareaRef.current.scrollTop = textareaRef.current.scrollHeight;
      }, 50);
    }
  }, [content, triggerAutoSave]);

  const handleCopyAll = () => {
    navigator.clipboard.writeText(content);
    toast({ title: 'Notepad copied to clipboard', status: 'success', duration: 2000 });
  };

  const handleClear = () => {
    if (window.confirm('Clear the entire notepad? This cannot be undone.')) {
      setContent('');
      setHasUnsavedChanges(true);
      triggerAutoSave('');
    }
  };

  const charCount = content.length;
  const wordCount = content.trim() ? content.trim().split(/\s+/).length : 0;

  return (
    <Box
      bg={bgColor}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      shadow="sm"
      overflow="hidden"
      display="flex"
      flexDirection="column"
      h={isCollapsed ? 'auto' : isFullHeight ? maxHeight : minHeight}
      transition="height 0.3s ease"
    >
      {/* Header */}
      <HStack
        px={4}
        py={3}
        bg={headerBg}
        borderBottomWidth="1px"
        borderColor={borderColor}
        justify="space-between"
        cursor="pointer"
        onClick={() => setIsCollapsed(!isCollapsed)}
        _hover={{ bg: useColorModeValue('gray.100', 'gray.700') }}
        transition="background 0.15s"
      >
        <HStack spacing={3}>
          <Icon as={FiFileText} color="blue.500" boxSize={5} />
          <Text fontWeight="bold" fontSize="sm">Deal Notepad</Text>
          {hasUnsavedChanges && (
            <Badge colorScheme="orange" variant="subtle" fontSize="xs">Unsaved</Badge>
          )}
          {isSaving && <Spinner size="xs" color="blue.400" />}
        </HStack>
        <HStack spacing={2}>
          {lastSaved && !hasUnsavedChanges && (
            <HStack spacing={1}>
              <Icon as={FiClock} color={savedColor} boxSize={3} />
              <Text fontSize="xs" color={savedColor}>
                Saved {moment(lastSaved).fromNow()}
              </Text>
            </HStack>
          )}
          <Icon as={isCollapsed ? FiChevronDown : FiChevronUp} />
        </HStack>
      </HStack>

      {/* Body */}
      <Collapse in={!isCollapsed} animateOpacity style={{ flex: 1, display: isCollapsed ? 'none' : 'flex', flexDirection: 'column' }}>
        <Box flex={1} display="flex" flexDirection="column" overflow="hidden">
          {/* Toolbar */}
          <HStack px={3} py={2} spacing={1} borderBottomWidth="1px" borderColor={borderColor} flexShrink={0}>
            <Tooltip label="Save now (Ctrl+S)">
              <IconButton
                icon={<FiSave />}
                size="xs"
                variant="ghost"
                colorScheme={hasUnsavedChanges ? 'orange' : 'gray'}
                onClick={(e) => { e.stopPropagation(); handleSave(); }}
                aria-label="Save"
              />
            </Tooltip>
            <Tooltip label="Copy all">
              <IconButton
                icon={<FiCopy />}
                size="xs"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); handleCopyAll(); }}
                aria-label="Copy all"
                isDisabled={!content}
              />
            </Tooltip>
            <Tooltip label={isFullHeight ? 'Compact view' : 'Full height'}>
              <IconButton
                icon={isFullHeight ? <FiMinimize2 /> : <FiMaximize2 />}
                size="xs"
                variant="ghost"
                onClick={(e) => { e.stopPropagation(); setIsFullHeight(!isFullHeight); }}
                aria-label="Toggle height"
              />
            </Tooltip>
            <Tooltip label="Clear notepad">
              <IconButton
                icon={<FiTrash2 />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={(e) => { e.stopPropagation(); handleClear(); }}
                aria-label="Clear"
                isDisabled={!content}
              />
            </Tooltip>
            <Box flex={1} />
            <Text fontSize="xs" color="gray.500">
              {wordCount} words · {charCount.toLocaleString()} chars
            </Text>
          </HStack>

          {/* Textarea */}
          <Textarea
            ref={textareaRef}
            value={content}
            onChange={handleContentChange}
            placeholder={`Start typing notes for this deal...\n\nTips:\n• This notepad auto-saves as you type\n• Activity logs will auto-append here\n• Use it like a running journal for the deal`}
            flex={1}
            resize="none"
            border="none"
            borderRadius={0}
            bg={textareaBg}
            _focus={{ bg: textareaFocusBg, boxShadow: 'none' }}
            _hover={{ bg: textareaFocusBg }}
            fontSize="sm"
            lineHeight="1.7"
            px={4}
            py={3}
            fontFamily="'Inter', -apple-system, sans-serif"
            overflow="auto"
            onKeyDown={(e) => {
              // Ctrl+S / Cmd+S to save
              if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                handleSave();
              }
            }}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
